interface FeedbackContext {
  isCorrect: boolean;
  streak: number;
  questionNumber: number;
  totalQuestions: number;
}

/**
 * Growth Mindset Motivational Messages (V6.0 - Simplified)
 *
 * RULES:
 * 1. NEVER use words: "Cito", "toets", "gokken", "fout"
 * 2. ALWAYS validate thinking before correcting
 * 3. Celebrate progress and effort
 */

export function getMotivationalMessage(context: FeedbackContext): string {
  const {
    isCorrect,
    streak
  } = context;
  if (isCorrect) {
    // High streak celebration
    if (streak >= 5) {
      const messages = ['Niet te stoppen! 🔥', 'Je bent on fire! 🚀', 'Wat een reeks! Geweldig! ⭐', 'Je gaat als een speer! ⚡', 'Ongelofelijk goed bezig! 🏆'];
      return messages[Math.floor(Math.random() * messages.length)];
    }

    // Standard correct messages
    const messages = ['Goed gedaan! 👍', 'Helemaal goed! 🌟', 'Lekker bezig! 💪', 'Top! Je snapt het! ✨', 'Precies goed! 🎯', 'Netjes! Ga zo door! 🚀', 'Uitstekend! 💎', 'Klopt helemaal! ✅', 'Super! Je groeit! 🌱', 'Mooi werk! 🎨'];
    return messages[Math.floor(Math.random() * messages.length)];
  } else {
    // Encouraging wrong answer messages
    const messages = ['Bijna! Laten we kijken waarom. 🔍', 'Nog niet helemaal. Je leert hiervan! 📚', 'Goede poging! Kijk naar de uitleg. 💡', 'Dit is een leermoment! 🎓', 'Niet erg, hiervan word je slimmer! 🧠', 'Nog even oefenen. Je komt er wel! 👣', 'Bijna goed! De uitleg helpt je. 📖', 'Geef niet op! Probeer het te snappen. 💪', 'Fouten maken mag! Zo leer je. 🌱', 'Laten we het samen uitpluizen! 🤝'];
    return messages[Math.floor(Math.random() * messages.length)];
  }
}

/**
 * Get checkpoint message based on performance
 */
export function getCheckpointMessage(correctCount: number, totalCount: number): string {
  const percentage = correctCount / totalCount * 100;
  if (percentage === 100) {
    return 'Alles goed! Je bent on fire! 🔥';
  } else if (percentage >= 80) {
    return 'Geweldig! Je bent goed bezig! 🌟';
  } else if (percentage >= 60) {
    return 'Mooi! Je groeit elke vraag! 💪';
  } else if (percentage >= 40) {
    return 'Je leert veel! Blijf doorgaan! 📈';
  } else {
    return 'Elke vraag is een stap vooruit! 🎯';
  }
}

/**
 * Get level-up title based on level
 */
export function getLevelTitle(level: number): string {
  if (level === 1) return 'Beginner';
  if (level === 2) return 'Verkenner';
  if (level === 3) return 'Kenner';
  if (level === 4) return 'Expert';
  if (level >= 5) return 'Meester';
  return 'Leerling';
}

/**
 * Get level-up message
 */
export function getLevelUpMessage(level: number): string {
  const messages: Record<number, string> = {
    2: 'Je groeit! Tijd voor nieuwe uitdagingen! 🚀',
    3: 'Wow! Je wordt steeds beter! 👑',
    4: 'Indrukwekkend! Je bent een echte expert! 📚',
    5: 'Legendarisch! Je bent een meester! ⭐'
  };
  return messages[level] || 'Je bent weer een level omhoog! Top! 🎉';
}

/**
 * Get streak celebration message
 */
export function getStreakMessage(streak: number): string {
  if (streak === 3) {
    return '🔥 3 op rij! Je bent lekker bezig!';
  } else if (streak === 5) {
    return '🔥 5 op rij! Je bent on fire!';
  } else if (streak === 7) {
    return '⚡ 7 op rij! Ongelofelijk!';
  } else if (streak === 10) {
    return '⚡ 10 op rij! Fenomenaal!';
  } else if (streak === 15) {
    return '🌟 15 op rij! Je bent een legende!';
  } else if (streak === 20) {
    return '👑 20 op rij! Meesterlijk!';
  } else if (streak % 5 === 0 && streak > 0) {
    return `🎯 ${streak} op rij! Blijf zo doorgaan!`;
  }
  return '';
}